import { DocumentNode, gql, useQuery } from '@apollo/client';
import { capitalize } from 'lodash';
import { useRouter } from 'next/router';
import * as queryString from 'query-string';
import * as React from 'react';

import { membership_checkout_started } from '@/analytics';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { MembershipTermLifecycleStatus } from '@/graphql-types/globalTypes';

import { PrePurchaseConfirmationModal_UserFragment } from './graphql-types/PrePurchaseConfirmationModal_UserFragment';

const MEMBERSHIP_SWITCH_PREVIEW = gql`
  query PrePurchaseConfirmationModal_MembershipSwitchPreview(
    $input: MembershipSwitchPreviewInput!
  ) {
    membershipSwitchPreview(input: $input) {
      switchType
    }
  }
`;

export const PrePurchaseConfirmationModal: React.FC<{
  user?: PrePurchaseConfirmationModal_UserFragment;
  isOpen: boolean;
  onClose: () => void;
  membershipTypeId: string | undefined;
  isFutureMembership?: boolean;
}> & {
  userFragment: DocumentNode;
} = ({
  user,
  isOpen,
  onClose,
  membershipTypeId,
  isFutureMembership = false,
}) => {
  const router = useRouter();

  const { data: membershipSwitchPreviewData } = useQuery<
    {
      membershipSwitchPreview: {
        switchType: string | null;
      } | null;
    },
    {
      input: {
        membershipTypeId: string;
        isFutureMembership?: boolean;
      };
    }
  >(MEMBERSHIP_SWITCH_PREVIEW, {
    variables: {
      input: {
        membershipTypeId: membershipTypeId ?? '',
        ...(isFutureMembership ? { isFutureMembership: true } : {}),
      },
    },
    skip: !user || !membershipTypeId || !isOpen,
    // Same reason as the RadioCard preview: never confirm against a switch
    // type computed before the member's membership last changed.
    fetchPolicy: 'cache-and-network',
  });

  const switchType =
    membershipSwitchPreviewData?.membershipSwitchPreview?.switchType;

  if (!user) {
    return null;
  }

  const hasUpcomingMembership = !!user.membershipData?.nextMembershipTerm;

  const isCurrentMembershipTermInRenewalPeriod =
    user.membershipData?.currentMembershipTerm?.lifecycleStatus ===
    MembershipTermLifecycleStatus.RENEWING;

  const voidUnpaidInvoicesMessage =
    'Any previous unpaid membership invoices will also be voided, whether or not a membership has been assigned.';

  const redirectToCheckout = async () => {
    if (!membershipTypeId) {
      throw new Error('Unexpected Missing Membership Type ID');
    }

    membership_checkout_started({
      entity_id: membershipTypeId,
      entity_type: 'membership_plan',
      membership_type_id: membershipTypeId,
      payment_item_type: 'membership',
      membership_change_type: switchType?.toLowerCase() || 'switch',
      page_type: 'membership',
      page_section: 'membership',
    });

    await router.push(
      isFutureMembership
        ? `/new-membership/${membershipTypeId}?${queryString.stringify({
            isFutureMembership: true,
          })}`
        : `/new-membership/${membershipTypeId}`
    );
  };

  return (
    <ConfirmDialog
      isOpen={isOpen}
      onClose={onClose}
      title={
        isFutureMembership
          ? 'Are you sure you want to purchase a Future-Dated Membership?'
          : switchType
          ? `Are you sure you want to ${capitalize(
              switchType.toLowerCase()
            )} your membership?`
          : isCurrentMembershipTermInRenewalPeriod
          ? 'Are you sure you want to switch your membership?'
          : 'Are you sure you want to purchase a new membership?'
      }
      body={
        isFutureMembership
          ? 'Doing this will add a future dated membership.'
          : isCurrentMembershipTermInRenewalPeriod
          ? hasUpcomingMembership
            ? `Doing this means your current membership will run its course and your queued membership will be replaced with the new membership you've selected.`
            : `Doing this means your current membership will run its course and a new membership will start with the new membership type you've selected.`
          : hasUpcomingMembership
          ? `Doing this will terminate your current and queued membership and start a new membership term. ${voidUnpaidInvoicesMessage}`
          : `Doing this will terminate your current membership and start a new membership term. ${voidUnpaidInvoicesMessage}`
      }
      confirmButtonText="Continue"
      onConfirm={redirectToCheckout}
      shouldCloseOnConfirmation={false}
    />
  );
};

PrePurchaseConfirmationModal.userFragment = gql`
  fragment PrePurchaseConfirmationModal_UserFragment on User {
    _id
    membershipData {
      currentMembershipTerm {
        _id
        lifecycleStatus
      }
      nextMembershipTerm {
        _id
      }
    }
  }
`;
